import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Box,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  Alert
} from '@cloudscape-design/components';

const TaskEditModal = ({ visible, onDismiss, onSave, task, familyMembers = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    type: 'task',
    start_time: '',
    end_time: '',
    points: 10,
    category: 'household',
    member_id: '',
    member_name: '',
    member_role: ''
  });

  // 当task改变时更新表单数据
  useEffect(() => {
    if (task) {
      setEditData({
        title: task.title || '',
        description: task.description || '',
        type: task.type || 'task',
        start_time: task.start_time || '',
        end_time: task.end_time || '',
        points: task.points || 10,
        category: task.category || 'household',
        member_id: task.member_id || '',
        member_name: task.member_name || '',
        member_role: task.member_role || ''
      });
    }
  }, [task]);

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMemberChange = (memberId) => {
    const member = familyMembers.find(m => m.id === memberId);
    if (member) {
      setEditData(prev => ({
        ...prev,
        member_id: member.id,
        member_name: member.name,
        member_role: member.role
      }));
    }
  };

  const handleSave = async () => {
    if (!editData.title.trim()) {
      setError('请输入任务标题');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const updatedTask = {
        ...task,
        ...editData,
        points: parseInt(editData.points) || 0
      };

      await onSave(updatedTask);
      onDismiss();
    } catch (error) {
      setError(error.message || '更新任务失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onDismiss();
  };

  const typeOptions = [
    { label: '任务', value: 'task' },
    { label: '计划', value: 'plan' },
    { label: '笔记', value: 'note' }
  ];

  const categoryOptions = [
    { label: '家务', value: 'household' },
    { label: '学习', value: 'study' },
    { label: '工作', value: 'work' },
    { label: '健康', value: 'health' },
    { label: '其他', value: 'other' }
  ];

  return (
    <Modal
      onDismiss={handleClose}
      visible={visible}
      size="large"
      header="编辑任务"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleClose}>
              取消
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSave}
              loading={loading}
            >
              保存更改
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="l">
          {error && (
            <Alert type="error" dismissible onDismiss={() => setError('')}>
              {error}
            </Alert>
          )}

          <FormField label="任务标题" stretch>
            <Input
              value={editData.title}
              onChange={({ detail }) => handleInputChange('title', detail.value)}
              placeholder="输入任务标题"
            />
          </FormField>

          <FormField label="任务描述" stretch>
            <Textarea
              value={editData.description}
              onChange={({ detail }) => handleInputChange('description', detail.value)}
              placeholder="输入任务描述（可选）"
              rows={3}
            />
          </FormField>

          <FormField label="分配给">
            <Select
              selectedOption={
                editData.member_id 
                  ? { 
                      label: editData.member_name, 
                      value: editData.member_id 
                    }
                  : null
              }
              onChange={({ detail }) => handleMemberChange(detail.selectedOption.value)}
              options={familyMembers.map(member => ({
                label: member.name,
                value: member.id
              }))}
              placeholder="选择家庭成员"
            />
          </FormField>

          <Box gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField label="任务类型">
              <Select
                selectedOption={typeOptions.find(opt => opt.value === editData.type)}
                onChange={({ detail }) => handleInputChange('type', detail.selectedOption.value)}
                options={typeOptions}
              />
            </FormField>

            <FormField label="分类">
              <Select
                selectedOption={categoryOptions.find(opt => opt.value === editData.category)}
                onChange={({ detail }) => handleInputChange('category', detail.selectedOption.value)}
                options={categoryOptions}
              />
            </FormField>
          </Box>

          <Box gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <FormField label="开始时间">
              <Input
                type="datetime-local"
                value={editData.start_time}
                onChange={({ detail }) => handleInputChange('start_time', detail.value)}
              />
            </FormField>

            <FormField label="结束时间">
              <Input
                type="datetime-local"
                value={editData.end_time}
                onChange={({ detail }) => handleInputChange('end_time', detail.value)}
              />
            </FormField>
          </Box>

          <FormField label="奖励金币">
            <Input
              type="number"
              value={editData.points.toString()}
              onChange={({ detail }) => handleInputChange('points', parseInt(detail.value) || 0)}
              placeholder="10"
              min="0"
            />
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default TaskEditModal;